import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import LinkIcon from '@mui/icons-material/Link'

import type { Entity } from '@models/Entity'
import type { FilterOption } from '@models/Filter'

import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { CreateEditProjectDialog } from '../../hoofdleiding/project/components/dialogs/CreateEditProjectDialog'
import type { HoofdleidingProject } from '../../hoofdleiding/project/types/HoofdleidingProject'
import type { GeblokkeerdeWeek } from '../../planning/types/GeblokkeerdeWeek'
import type { Projectmelding } from '../../projectmelding/types/Projectmelding'
import { StyledIconButton } from '../../theme/components/StyledIconButton'
import { GenericLinkedProjects } from '../components/GenericLinkedProjects'
import { useUnlinkProjectFromProjectdossier } from '../hooks/useUnlinkProjectFromProjectdossier'
import type { Projectdossier } from '../types/Projectdossier'

import { LinkProjectDialog } from './dialogs/LinkProjectDialog'

type Props = {
  entity: Entity
  linkedProjectIds: string[]
  projectmelding?: Projectmelding
  projectdossier: Projectdossier
  hoofdleidingGebieden: FilterOption[]
  geblokkeerdeWeken: GeblokkeerdeWeek[]
}

export const LinkedProjects = ({
  entity,
  linkedProjectIds,
  projectmelding,
  projectdossier,
  hoofdleidingGebieden,
  geblokkeerdeWeken,
}: Props) => {
  const { mutate: unlinkProjectFromProjectdossier } =
    useUnlinkProjectFromProjectdossier()
  const { canCreateProjectdossier, combi } = useAuthenticatedUser()
  const [open, setIsOpen] = useState(false)
  const [openCreate, setOpenCreate] = useState(false)
  const isVerantwoordelijke = combi !== undefined

  const [projectInfo, setProjectInfo] = useState<
    Partial<HoofdleidingProject> | undefined
  >()

  const showCreateProject = hoofdleidingGebieden.some(
    (gebied) => gebied.code === projectdossier.gebiedscode,
  )

  const onClickCreateProject = () => {
    const data: Record<string, any> = {
      linkedProjectdossier: entity.id,
      gebiedscode: projectdossier.gebiedscode,
    }

    if (projectdossier) {
      data.naam = projectdossier.naam
    }

    if (
      projectmelding &&
      projectmelding.derivativeProjectdossier === entity.id
    ) {
      data.aanvraagDatum = projectmelding.created
      data.adres = ''
    }
    setProjectInfo(data)

    setOpenCreate(true)
  }

  const handleUnlink = (id: string, unlinkId: string) => {
    unlinkProjectFromProjectdossier({ id, projectId: unlinkId })
  }

  return (
    <>
      <GenericLinkedProjects
        entity={entity}
        linkedProjectIds={linkedProjectIds}
        isVerantwoordelijke={isVerantwoordelijke}
        unlinkHandler={handleUnlink}
        toolbar={
          <>
            {canCreateProjectdossier && (
              <>
                <StyledIconButton
                  title="Koppel een nieuw project"
                  onClick={() => setIsOpen(true)}
                  icon={<LinkIcon />}
                />
                {showCreateProject && (
                  <StyledIconButton
                    title="Aanmaken hoofdleiding project"
                    onClick={onClickCreateProject}
                    icon={<AddIcon />}
                  />
                )}
              </>
            )}
          </>
        }
      />

      {open && (
        <LinkProjectDialog
          dossierId={entity.id}
          onClose={() => setIsOpen(false)}
        />
      )}

      {openCreate && (
        <CreateEditProjectDialog
          handleClose={() => setOpenCreate(false)}
          projectInfo={projectInfo}
          type="create"
          geblokkeerdeWeken={geblokkeerdeWeken}
        />
      )}
    </>
  )
}
